import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Checkbox, FlexColumn, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../../shared/util/util';
import UpdateProjectPortalConfigMutation from '../../../../mutations/update_project_portal_config_mutation';

/**
 * FOR-48 (BU-10) 10b — Project Settings → Client Portal tab. The ONLY project-level
 * portal controls: "Show in client portal" (publishes this project to the client's
 * portal) + phase-status visibility. Access configuration (activation, branding,
 * feature flags) is Client Organisation level (10a), NOT here.
 *
 * Writes go through the portal proxy mutation keyed by the project's PSA id
 * (companyProjectId — the identifier the PSA feed syncs as psa_project_id). No
 * forecast_db write.
 *
 * The client and the project's own name/status ride along on every write. The portal
 * has no row for either until a sync has run for the firm, and publishing is usually
 * the first portal action taken for a project, so it provisions both from this
 * metadata on first touch. A project with NO client cannot be published at all —
 * there is no client workspace to publish it into — so the controls are disabled
 * rather than left clickable against a write the portal would refuse.
 */
const ProjectClientPortal = ({psaProjectId, portalConfig, psaClientExternalId, clientName, projectName, projectStatus}) => {
	const {formatMessage} = useIntl();
	const [showInClientPortal, setShowInClientPortal] = useState(!!portalConfig?.showInClientPortal);
	const [phaseStatusVisible, setPhaseStatusVisible] = useState(
		portalConfig?.portalPhaseStatusVisible == null ? true : !!portalConfig.portalPhaseStatusVisible
	);
	const hasClient = !!psaClientExternalId;

	const save = next => {
		Util.CommitMutation(UpdateProjectPortalConfigMutation, {
			psaProjectId: String(psaProjectId),
			showInClientPortal: next.showInClientPortal,
			portalPhaseStatusVisible: next.phaseStatusVisible,
			psaClientExternalId,
			// Omitted when absent so the portal keeps whatever name it already has,
			// rather than overwriting it with an empty string.
			...(clientName ? {clientName} : {}),
			...(projectName ? {projectName} : {}),
			...(projectStatus ? {projectStatus} : {}),
		});
	};

	const onToggleShow = () => {
		if (!hasClient) return;
		const value = !showInClientPortal;
		setShowInClientPortal(value);
		save({showInClientPortal: value, phaseStatusVisible});
	};

	const onTogglePhaseStatus = () => {
		if (!hasClient) return;
		const value = !phaseStatusVisible;
		setPhaseStatusVisible(value);
		save({showInClientPortal, phaseStatusVisible: value});
	};

	return (
		<FlexColumn gap={'l'} data-cy="project-client-portal-tab">
			{hasClient ? null : (
				<Text color={'secondary'} data-cy="no-client-assigned-notice">
					<FormattedMessage
						id="client_portal.no_client_assigned"
						defaultMessage="Assign a client to this project before publishing it. A project can only appear in the workspace of the client it belongs to."
					/>
				</Text>
			)}
			{/* Checkbox renders `labelText`, not children — children passed to it are
			    dropped, leaving an unlabelled box. */}
			<Checkbox
				checked={showInClientPortal}
				onChange={onToggleShow}
				disabled={!hasClient}
				data-cy="show-in-client-portal-toggle"
				labelText={formatMessage({id: 'client_portal.show_in_portal', defaultMessage: 'Show in client workspace'})}
			/>
			<Text color={'secondary'}>
				<FormattedMessage
					id="client_portal.show_in_portal_help"
					defaultMessage="Publishes this project to the client's workspace. Does not affect sibling projects or the client's workspace activation."
				/>
			</Text>
			<Checkbox
				checked={phaseStatusVisible}
				onChange={onTogglePhaseStatus}
				disabled={!hasClient}
				data-cy="phase-status-visible-toggle"
				labelText={formatMessage({
					id: 'client_portal.phase_status_visible',
					defaultMessage: 'Show phase status in workspace',
				})}
			/>
		</FlexColumn>
	);
};

ProjectClientPortal.propTypes = {
	psaProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	portalConfig: PropTypes.shape({
		showInClientPortal: PropTypes.bool,
		portalPhaseStatusVisible: PropTypes.bool,
	}),
	// The client's Relay global id; the proxy decodes it to the raw Forecast client
	// id the portal keys client orgs by. Absent for a project with no client.
	psaClientExternalId: PropTypes.string,
	clientName: PropTypes.string,
	projectName: PropTypes.string,
	projectStatus: PropTypes.string,
};

export default ProjectClientPortal;
